import { useState } from 'react';
import { useNavigate } from 'react-router';

import {
  ROUTE_IDS,
  RouteId,
  usePartnerApplicationById,
} from '~domains/partner-application/entities/application';
import { PartnerApplicationMachineContext } from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_REVIEW,
} from '~domains/partner-application/shared/constants';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';
import { useApplicationActions } from '~domains/partner-application/widgets/partner-application';

import { ampli } from '~shared/analytics';
import { ROUTES } from '~shared/config/routes';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { ScreenHeader, Screen } from '~shared/ui/screen';

import { Typography } from '@breezeventures/ui-kit/typography';

import { AgentsReview } from './agents-review';
import { BankAccountReview } from './bank-account-review';
import { BusinessDetailsReview } from './business-details-review';
import { BusinessDocumentsReview } from './business-documents-review';
import { OfficeAddressReview } from './office-address-review';
import { RegisteredAddressReview } from './registered-address-review';
import { ReviewSkeleton } from './review-skeleton';
import { StoresReview } from './stores-review';

export const Review = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const actor = PartnerApplicationMachineContext.useActorRef();
  const applicationId = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.applicationId
  );
  const { submitApplication } = useApplicationActions();
  const applicationQuery = usePartnerApplicationById({
    applicationId,
  });

  const handleSubmit = async () => {
    ampli.mpPaReviewSubmitApplicationClick(
      getApplicationEventProperties(applicationId)
    );
    try {
      setIsSubmitting(true);
      await submitApplication();
      actor.send({ type: 'review.submit' });
      navigate(ROUTES.partner.applicationMenu, { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (routeId: RouteId) => {
    const eventProperties = getApplicationEventProperties(applicationId);
    if (routeId === ROUTE_IDS.businessDetails) {
      ampli.mpPaReviewEditBusinessDetailsClick(eventProperties);
    } else if (routeId === ROUTE_IDS.businessAddress) {
      ampli.mpPaReviewEditRegisteredBusinessAddressClick(eventProperties);
    } else if (routeId === ROUTE_IDS.officeAddress) {
      ampli.mpPaReviewEditMainOfficeAddressClick(eventProperties);
    } else if (routeId === ROUTE_IDS.bankDetails) {
      ampli.mpPaReviewEditBankDetailsClick(eventProperties);
    } else if (routeId === ROUTE_IDS.businessDocuments) {
      ampli.mpPaReviewEditBusinessDocumentsClick(eventProperties);
    } else if (routeId === ROUTE_IDS.stores) {
      ampli.mpPaReviewEditStoresClick(eventProperties);
    } else if (routeId === ROUTE_IDS.agents) {
      ampli.mpPaReviewEditAgentsClick(eventProperties);
    }
    actor.send({ type: 'xstate.route', to: routeId });
  };

  const onClose = () => navigate(ROUTES.partner.applicationMenu);

  return (
    <Screen
      header={
        <ScreenHeader
          onBack={() => actor.send({ type: 'back' })}
          onClose={onClose}
        />
      }
      footer={
        <FooterWithProgressBar
          currentStepIndex={STEP_INDEX_REVIEW}
          totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-3 py-6">
        <Typography.Title size="s">Review your details</Typography.Title>
        <Typography.Text size="m" color="secondary">
          Make sure everything is correct before you submit.
        </Typography.Text>
      </div>

      {applicationQuery.isLoading && !applicationQuery.data && (
        <ReviewSkeleton />
      )}

      {applicationQuery.data && (
        <div className="flex flex-col gap-3">
          <BusinessDetailsReview
            businessInfo={applicationQuery.data.businessInfo}
            ownership={applicationQuery.data.ownership.type}
            onEdit={() => handleEdit(ROUTE_IDS.businessDetails)}
          />
          <RegisteredAddressReview
            registeredAddress={applicationQuery.data.registeredAddress}
            onEdit={() => handleEdit(ROUTE_IDS.businessAddress)}
          />
          <OfficeAddressReview
            officeAddress={applicationQuery.data.headOfficeAddress}
            registeredAddress={applicationQuery.data.registeredAddress}
            onEdit={() => handleEdit(ROUTE_IDS.officeAddress)}
          />
          <BankAccountReview
            bankDetails={applicationQuery.data.bankDetails}
            onEdit={() => handleEdit(ROUTE_IDS.bankDetails)}
          />
          <BusinessDocumentsReview
            businessDocuments={applicationQuery.data.businessDocuments}
            onEdit={() => handleEdit(ROUTE_IDS.businessDocuments)}
          />
          <StoresReview
            pointsOfSales={applicationQuery.data.pointsOfSales}
            onEdit={() => handleEdit(ROUTE_IDS.stores)}
          />
          <AgentsReview
            agents={applicationQuery.data.agents}
            pointsOfSales={applicationQuery.data.pointsOfSales}
            onEdit={() => handleEdit(ROUTE_IDS.agents)}
          />
        </div>
      )}
    </Screen>
  );
};
